import type { IFieldInputStrategy } from '@/Core/Contracts/UI/IFieldInputStrategy'
import { FieldInputStrategyRegistry } from '@/Core/Contracts/UI/IFieldInputStrategy'
import { UIManager } from '@/Core/Managers/UI'
import { action, computed, makeObservable, observable, toJS } from 'mobx'
import { FieldStrategy, FieldType, FieldValidator, IFieldDefinition } from './Types/FieldTypeEnum'

/**
 * Базовий клас для всіх типів полів
 * Повнофункціональна версія з підтримкою всіх можливостей зі старого ядра
 */
export class BaseFieldStore extends UIManager {
    // Основні властивості нової архітектури
    definition: IFieldDefinition
    formContext: any
    path: string[]
    strategy: FieldStrategy

    // Стан поля
    isRequired = false
    isEditable = true
    isTranslatable = false
    isDisableTransform = false
    inputProps: Record<string, any> = {}
    depFn: (() => boolean) | null = null
    tooltip?: string
    
    // Додаткові властивості для розширеного функціоналу
    valueName: string = 'value'
    validators: FieldValidator[] = []
    access: any = null
    debounceTimeoutId: any = null
    props: Record<string, any> = {}
    
    // Стратегія обробки специфічної логіки типу поля
    private inputStrategy: IFieldInputStrategy | null = null

    constructor(definition: IFieldDefinition, formContext: any, path: string[] = []) {
        super()

        this.definition = definition
        this.formContext = formContext
        this.path = [...path, definition.name]
        this.strategy = this.getStrategy(definition.type)

        // Застосовуємо налаштування з визначення
        if (definition.isRequired !== undefined) {
            this.isRequired = definition.isRequired
        }

        // Для обчислювальних полів (COMPUTED) за замовчуванням isEditable = false
        if (definition.type === FieldType.COMPUTED) {
            this.isEditable = definition.isEditable !== undefined ? definition.isEditable : false
        } else if (definition.isEditable !== undefined) {
            this.isEditable = definition.isEditable
        }

        if (definition.isTranslatable !== undefined) {
            this.isTranslatable = definition.isTranslatable
        }

        if (definition.isDisableTransform !== undefined) {
            this.isDisableTransform = definition.isDisableTransform
        }

        // Для обчислювальних полів додаємо disabled в inputProps за замовчуванням
        if (definition.type === FieldType.COMPUTED) {
            this.inputProps = {
                disabled: true,
                ...definition.inputProps, // Користувач може перевизначити
            }
        } else if (definition.inputProps) {
            this.inputProps = { ...definition.inputProps }
        }

        if (definition.validators) {
            this.validators = definition.validators
        }

        if (definition.access !== undefined) {
            this.access = definition.access
        }

        if (definition.valueName) {
            this.valueName = definition.valueName
        }

        if (definition.props) {
            this.props = { ...definition.props }
        }

        // Зберігаємо depFn з визначення
        if (definition.depFn) {
            this.depFn = definition.depFn
        }

        // Зберігаємо tooltip з визначення
        if (definition.tooltip) {
            this.tooltip = definition.tooltip
        }

        // Копіюємо options в props для select/radio полів
        if (definition.options && Array.isArray(definition.options)) {
            if (!this.props) {
                this.props = {}
            }
            this.props.options = definition.options
        }

        // Ініціалізуємо стратегію для типу поля
        this.inputStrategy = FieldInputStrategyRegistry.get(this.type) || null

        // Якщо стратегія визначає valueName, використовуємо його
        if (this.inputStrategy && !definition.valueName) {
            this.valueName = this.inputStrategy.getValueName?.(this) || 'value'
        }

        if (definition.defaultValue !== undefined) {
            this.formContext.setInitialValue(this.path, definition.defaultValue)
        }

        makeObservable(this, {
            isRequired: observable,
            isEditable: observable,
            isTranslatable: observable,
            isDisableTransform: observable,
            inputProps: observable,
            valueName: observable,
            props: observable,
            tooltip: observable,
            value: computed,
            initialValue: computed,
            error: computed,
            isValid: computed,
            needsMultipleWrapper: computed,
            setIsRequired: action,
            setIsEditable: action,
            setIsTranslatable: action,
            setIsDisableTransform: action,
            setInputProps: action,
            setLabel: action,
            setValue: action,
            setView: action,
            setDebounceTimeoutId: action,
            mergeInputProps: action,
            mergeProps: action,
        })
    }

    /**
     * Определяет стратегию для типа поля
     */
    private getStrategy(type: FieldType): FieldStrategy {
        if (type === FieldType.ARRAY) return FieldStrategy.ARRAY
        if (type === FieldType.OBJECT) return FieldStrategy.OBJECT
        if (type === FieldType.TABLE) return FieldStrategy.TABLE
        return FieldStrategy.PRIMITIVE
    }

    // Computed властивості
    get value(): any {
        const value = this.formContext.getValue(this.path)
        const rawValue = value !== undefined ? value : this.definition.defaultValue
        return this.valueTransformer(rawValue)
    }

    get initialValue(): any {
        return this.formContext.getInitialValue(this.path)
    }

    get error(): string | null {
        return this.formContext.getError(this.path)
    }

    get isValid(): boolean {
        return this.error === null
    }

    get label(): string {
        return this.definition.label
    }

    get name(): string {
        return this.definition.name
    }

    get type(): FieldType {
        return this.definition.type
    }

    /**
     * Перевіряє, чи потрібен FormMultipleField для множинного поля
     * Якщо поле підтримує власну множинність (наприклад, Select), повертає false
     */
    get needsMultipleWrapper(): boolean {
        if (!this.definition.isMultiple) {
            return false
        }
        
        // Якщо стратегія підтримує власну множинність, wrapper не потрібен
        if (this.inputStrategy?.supportsNativeMultiple === true) {
            return false
        }
        
        return true
    }

    // Actions
    setIsRequired(required: boolean): void {
        this.isRequired = required
    }

    setIsEditable(isEditable: boolean): void {
        this.isEditable = isEditable
    }

    setIsTranslatable(isTranslatable: boolean): void {
        this.isTranslatable = isTranslatable
    }

    setIsDisableTransform(isDisableTransform: boolean): void {
        this.isDisableTransform = isDisableTransform
    }

    setLabel(label: string): void {
        this.definition.label = label
    }

    setView(view: (value: any, context: any) => any): void {
        if (typeof view === 'function') {
            this.definition.view = view
        }
    }

    setDebounceTimeoutId(debounceTimeoutId: any): void {
        this.debounceTimeoutId = debounceTimeoutId
    }

    setInputProps(props: Record<string, any>): void {
        if (typeof props === 'object') {
            this.inputProps = { ...props }
        }
    }

    mergeInputProps(props: Record<string, any>): void {
        if (typeof props === 'object') {
            // Порівняння примітивних значень без циклічних посилань
            const isPrimitiveEqual = (a: any, b: any): boolean => {
                if (a === b) return true
                if (a === null || b === null) return a === b

                const typeA = typeof a
                const typeB = typeof b

                if (typeA !== typeB) return false

                if (typeA === 'string' || typeA === 'number' || typeA === 'boolean') {
                    return a === b
                }

                // Пропускаємо React вузли та HTML елементи
                if (typeA === 'object') {
                    // Перевірка на React елементи
                    if ((a && (a as any).$$typeof) || (b && (b as any).$$typeof)) return true

                    // Перевірка на DOM елементи
                    if (
                        (typeof Element !== 'undefined' && a instanceof Element) ||
                        (typeof Element !== 'undefined' && b instanceof Element)
                    )
                        return true

                    // Перевірка на інші складні об'єкти з можливими циклічними посиланнями
                    if (
                        (a &&
                            typeof a === 'object' &&
                            a.constructor !== Object &&
                            a.constructor !== Array) ||
                        (b &&
                            typeof b === 'object' &&
                            b.constructor !== Object &&
                            b.constructor !== Array)
                    )
                        return true
                }

                return false
            }

            let hasChanges = false

            const existingKeys = Object.keys(toJS(this.inputProps))
            const newKeys = Object.keys(props)

            if (existingKeys.length !== newKeys.length) {
                hasChanges = true
            } else {
                for (const key in props) {
                    if (!isPrimitiveEqual(props[key], this.inputProps[key])) {
                        hasChanges = true
                        break
                    }
                }
            }

            if (hasChanges) {
                this.setInputProps({ ...toJS(this.inputProps), ...props })
            }
        }
    }

    mergeProps(props: Record<string, any>): void {
        if (typeof props === 'object') {
            this.props = { ...this.props, ...props }
        }
    }

    setValue(value: any): void {
        this.formContext.setValue(this.path, value)
    }

    /**
     * Створює props для прив'язки до React компонентів
     */
    bind(props: any = {}): Record<string, any> {
        let value = this.value
        const valueName = this.valueName || this.getValueName()

        // Обробка множинних значень (isMultiple)
        if (this.definition.isMultiple === true && props?.index !== undefined) {
            if (value !== undefined && Array.isArray(value) && value[props.index] !== undefined) {
                value = value[props.index]
            }
        }

        // Клонуємо inputProps і видаляємо конфліктуючі властивості
        const cleanInputProps = { ...this.inputProps }
        
        // Якщо valueName !== 'value', видаляємо 'value' з inputProps
        // щоб не перезаписати checked/інше значення
        if (valueName !== 'value' && 'value' in cleanInputProps) {
            delete cleanInputProps.value
        }

        const baseProps = {
            [valueName]: value,
            onChange: (e: any) => {
                let newValue = this.extractValue(e)

                // Обробка множинних значень
                if (this.definition.isMultiple && props.index !== undefined) {
                    const prevValue = this.formContext.getValue(this.path) ?? []

                    if (!Array.isArray(prevValue)) {
                        newValue = [newValue]
                    } else {
                        // Створюємо новий масив замість мутації існуючого
                        const updatedArray = [...prevValue]
                        updatedArray[props.index] = newValue
                        newValue = updatedArray
                    }
                }

                this.setValue(newValue)

                if (typeof props?.onChange === 'function') {
                    props.onChange(e)
                }
            },
            ...cleanInputProps,
            ...props,
        }

        // Дозволяємо стратегії модифікувати props
        if (this.inputStrategy?.modifyInputProps) {
            return this.inputStrategy.modifyInputProps(baseProps, this)
        }

        return baseProps
    }

    /**
     * Визначає ім'я властивості значення для React компонентів
     */
    private getValueName(): string {
        // Якщо є стратегія, використовуємо її
        if (this.inputStrategy?.getValueName) {
            return this.inputStrategy.getValueName(this)
        }
        // Дефолтне значення
        return 'value'
    }

    /**
     * Витягує значення з події або об'єкта
     */
    private extractValue(e: any): any {
        // Якщо є стратегія, використовуємо її
        if (this.inputStrategy?.extractValue) {
            return this.inputStrategy.extractValue(e, this)
        }

        // Дефолтна логіка (для string та інших простих типів)
        if (e?.target) {
            const valueName = this.getValueName()
            return e.target[valueName]
        }

        return e
    }

    /**
     * Головний трансформер значень
     * Визначає, який трансформер використовувати залежно від режиму редагування
     */
    valueTransformer(value: any): any {
        return this.formContext.isEditable && this.isEditable
            ? this.valueEditTransformer(value)
            : this.valueViewTransformer(value)
    }

    /**
     * Трансформер для режиму редагування
     * Перетворює значення для відображення в input компонентах
     */
    valueEditTransformer(value: any): any {
        // Якщо є стратегія, використовуємо її
        if (this.inputStrategy?.transformForEdit) {
            return this.inputStrategy.transformForEdit(value, this)
        }

        // Дефолтна логіка (для базових типів)
        value = toJS(value)

        if (this.isDisableTransform) {
            return value
        }

        // Кастомний input трансформер
        if (typeof this.definition.input === 'function') {
            return this.definition.input(value)
        }

        // Загальна обробка об'єктів (API ресурсів)
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            value =
                typeof this.props?.responseValue === 'function'
                    ? this.props.responseValue(value)
                    : value['@id'] !== undefined
                      ? value['@id']
                      : value
        } else if (Array.isArray(value)) {
            value = value.map((i) => {
                let vi = i
                if (typeof i === 'object' && i && i['@id'] !== undefined) {
                    vi = i['@id']
                }
                return vi
            })
        }

        return value
    }

    /**
     * Трансформер для режиму перегляду
     * Перетворює значення для читабельного відображення
     */
    valueViewTransformer(value: any): any {
        // Якщо є стратегія, використовуємо її
        if (this.inputStrategy?.transformForView) {
            return this.inputStrategy.transformForView(value, this)
        }

        // Дефолтна логіка (для базових типів)
        if (this.isDisableTransform) {
            return toJS(value)
        }

        // Кастомний view трансформер
        if (typeof this.definition.view === 'function') {
            return this.definition.view(toJS(value), this.formContext)
        }

        let view = toJS(value)

        // Загальна обробка об'єктів
        if (typeof view === 'object' && !Array.isArray(view) && view !== null) {
            if (view['fullName'] !== undefined) {
                view = view['fullName']
            } else if (view['lastName'] !== undefined) {
                view = `${view['lastName']} ${view['firstName']}`
            } else if (view['title'] !== undefined) {
                view = view['title']
            } else if (view['name'] !== undefined) {
                view = view['name']
            } else {
                // Fallback: use @id for API resource stubs that only have @id + @type
                view = view['@id'] || '-'
            }
        } else if (Array.isArray(view)) {
            view = view.map((i) => {
                let v = i
                if (typeof i === 'object') {
                    if (i['fullName'] !== undefined) {
                        v = i['fullName']
                    } else if (i['lastName'] !== undefined) {
                        v = `${i['lastName']} ${i['firstName']}`
                    } else if (i['title'] !== undefined) {
                        v = i['title']
                    } else if (i['name'] !== undefined) {
                        v = i['name']
                    } else {
                        // Fallback: use @id for API resource stubs that only have @id + @type
                        v = i['@id'] || '-'
                    }
                }
                return v
            })
        }

        // Якщо значення порожнє
        if (!view || (Array.isArray(view) && view.length === 0)) {
            view = '-'
        }

        return view
    }

    /**
     * Валідація поля
     */
    validate(): boolean {
        // Перевірка обов'язкового поля
        if (this.isRequired) {
            const value = this.value

            if (value === undefined || value === null || value === '') {
                const errorMessage = `Поле "${this.label}" є обов'язковим для заповнення`
                this.formContext.setError(this.path, errorMessage)
                return false
            }

            if (Array.isArray(value) && value.length === 0) {
                const errorMessage = `Поле "${this.label}" є обов'язковим для заповнення`
                this.formContext.setError(this.path, errorMessage)
                return false
            }
        }

        // Валідація специфічна для типу поля (через стратегію)
        if (this.inputStrategy?.validate) {
            const error = this.inputStrategy.validate(this.value, this)
            if (error) {
                this.formContext.setError(this.path, error)
                return false
            }
        }

        // Кастомні валідатори
        if (this.validators && this.validators.length > 0) {
            for (const validator of this.validators) {
                const error = validator(this.value, this)
                if (error) {
                    this.formContext.setError(this.path, error)
                    return false
                }
            }
        }

        // Очищуємо помилку якщо валідація пройшла
        this.formContext.setError(this.path, null)
        return true
    }
}
